/**
 * Web-push plumbing shared by the Settings page, the value-first prompt and
 * the silent re-registration on app open.
 *
 * The server keeps ONE subscription PER DEVICE (keyed by the push-service
 * endpoint). Each device must therefore re-register itself now and then, or
 * a subscription that the browser rotated, or that the server dropped after a
 * dead-endpoint response, is never replaced and that device goes quiet.
 */
import { isTWA } from './platform';

const REREGISTER_EVERY_MS = 12 * 60 * 60 * 1000;

export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export const webPushSupported = (): boolean =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/**
 * The browser's existing subscription, or a new one when permission is
 * already granted. Never prompts: `subscribe()` only shows a prompt when the
 * permission is still 'default', and we do not call it then.
 */
export async function getWebPushSubscription(): Promise<PushSubscription | null> {
  if (!webPushSupported()) return null;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  if (Notification.permission === 'denied') return null;
  // A browser that has never been asked must not be prompted from the
  // background — the Settings page and the value-first prompt own the ask.
  // The one exception is the Android TWA, where `Notification.permission`
  // reads 'default' even when the app-level permission is granted; there
  // subscribe() succeeds silently, and if it is truly blocked it throws. Cap
  // that attempt to one per 12 hours so a blocked device is not nagged.
  if (Notification.permission !== 'granted') {
    if (!isTWA()) return null;
    const lastAttempt = Number(localStorage.getItem(ATTEMPT_AT_KEY) || 0);
    if (Date.now() - lastAttempt < REREGISTER_EVERY_MS) return null;
    localStorage.setItem(ATTEMPT_AT_KEY, String(Date.now()));
  }
  const key = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';
  if (!key) return null;
  try {
    return await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  } catch {
    return null;
  }
}

const ATTEMPT_AT_KEY = 'fueltron_webpush_attempt_at';

/** POST the subscription to the server under this device's endpoint. */
export async function registerWebPushSubscription(apiBase: string, subscription: PushSubscription): Promise<boolean> {
  const token = localStorage.getItem('token');
  if (!token) return false;
  const res = await fetch(`${apiBase}/notifications/register-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      token: JSON.stringify(subscription),
      platform: isTWA() ? 'android' : 'web',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || undefined,
    }),
  });
  return res.ok;
}

const REGISTERED_AT_KEY = 'fueltron_webpush_registered_at';

/**
 * Silently keep THIS device's subscription registered — at most every 12
 * hours, never prompting. Called on every app open. Returns true when a
 * registration was sent.
 */
export async function refreshWebPushRegistration(apiBase: string): Promise<boolean> {
  try {
    if (!webPushSupported()) return false;
    const last = Number(localStorage.getItem(REGISTERED_AT_KEY) || 0);
    if (Date.now() - last < REREGISTER_EVERY_MS) return false;
    const sub = await getWebPushSubscription();
    if (!sub) return false;
    const ok = await registerWebPushSubscription(apiBase, sub);
    if (ok) localStorage.setItem(REGISTERED_AT_KEY, String(Date.now()));
    return ok;
  } catch {
    return false;
  }
}
